import { FbeForm, FbeSection, LocalisedText } from './fbeTypes';

// ── Low-level helpers ─────────────────────────────────────────────────────────

export const hasMeaningfulText = (value: string | undefined): boolean => {
  if (!value) return false;
  return value
    .replace(/<[^>]*>/g, '')
    .replace(/(&nbsp;|&#160;|\u00A0)/g, '')
    .trim()
    .length > 0;
};

export const getLocalisedText = (
  texts: LocalisedText[] | undefined,
  lang: string,
): string => (texts || []).find((t) => t.language === lang)?.text || '';

export const upsertLocalisedText = (
  texts: LocalisedText[],
  lang: string,
  value: string,
): LocalisedText[] => {
  if (!hasMeaningfulText(value)) return texts.filter((t) => t.language !== lang);
  const exists = texts.some((t) => t.language === lang);
  if (exists) return texts.map((t) => (t.language === lang ? { ...t, text: value } : t));
  return [...texts, { language: lang, text: value }];
};

// ── Core computation ──────────────────────────────────────────────────────────

/**
 * Computes translation completion percentage for every language in
 * `form.languages`, relative to the source language (`form.defaultLanguage`).
 *
 * The denominator for a given target language is: the count of translatable
 * fields that have meaningful content in the SOURCE language.  Only those
 * fields need to be translated; fields that are blank in the source are skipped.
 *
 * Returns `Record<languageCode, 0-100>`.
 * The source language itself is always returned as 100.
 */
export const computeFormTranslationCompletion = (
  form: FbeForm,
  sections: FbeSection[],
): Record<string, number> => {
  const srcLang =
    form.defaultLanguage ||
    form.name?.[0]?.language ||
    'en';

  const supportedLanguages: string[] = form.languages || [];
  const result: Record<string, number> = {};

  for (const lang of supportedLanguages) {
    if (lang === srcLang) {
      result[lang] = 100;
      continue;
    }

    let total = 0;
    let filled = 0;

    /** Check one LocalisedText[] field: if source has content, it counts toward total. */
    const check = (texts: LocalisedText[] | undefined) => {
      const srcText = getLocalisedText(texts, srcLang);
      if (!hasMeaningfulText(srcText)) return;
      total++;
      if (hasMeaningfulText(getLocalisedText(texts, lang))) filled++;
    };

    // ── Form-level fields ───────────────────────────────────────────────────
    check(form.name);

    // description: only count if it is stored as a proper LocalisedText[] array.
    // The legacy plain-string `form.description` is NOT counted — it cannot be
    // translated via the UI and its presence would inflate the denominator.
    if (form.descriptions && form.descriptions.length > 0) {
      check(form.descriptions);
    }

    // ── Section + question fields ───────────────────────────────────────────
    for (const section of sections) {
      check(section.name);

      const secDescTexts: LocalisedText[] =
        section.descriptions && section.descriptions.length > 0
          ? section.descriptions
          : section.description
          ? [{ language: srcLang, text: section.description }]
          : [];
      check(secDescTexts);

      if (section.mergeMetaData?.description) {
        check(section.mergeMetaData.description);
      }

      if (section.loop?.name && section.loop.name.length > 0) {
        check(section.loop.name);
      }

      for (const question of section.questions || []) {
        check(question.question);
        if (question.questionShort) check(question.questionShort);
        if (question.additionalInfo) check(question.additionalInfo);
        for (const answer of question.answers || []) {
          check(answer.answer);
        }
      }
    }

    result[lang] = total > 0 ? Math.round((filled / total) * 100) : 100;
  }

  return result;
};
